import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { LegalDoc } from '../data/live';
import { Empty, Loading, Tile } from '../components/ui';
import { errorText } from '../lib/errors';

/* The Terms & Conditions and Privacy Policy the driver app shows, and the
   Counter Terms Spark Agent shows. Saving publishes a NEW version and the apps
   read it on their next open — no app release needed. Earlier versions are
   kept, because people agreed to the words as they stood (migrations 049, 050).

   "Ask everyone to agree again" decides whether a version is a change people
   must accept, or a correction. Unticked, everyone who had agreed to the
   version before is carried forward and nobody is asked. */

const LABEL: Record<LegalDoc['kind'], string> = {
  terms: 'Terms & Conditions',
  privacy: 'Privacy Policy',
  agent_terms: 'Counter Terms',
};
const WHO: Partial<Record<LegalDoc['kind'], string>> = { terms: 'drivers', agent_terms: 'counters' };

function when(at?: string) {
  return at ? new Date(at).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
}

function Editor({ doc }: { doc: LegalDoc }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(doc.title ?? '');
  const [body, setBody] = useState(doc.body ?? '');
  /* Ticked by default: a change to what people agreed to is the case that
     must not slip through unasked. A typo is the one you untick for. */
  const [askAgain, setAskAgain] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setTitle(doc.title ?? ''); setBody(doc.body ?? ''); setAskAgain(true);
  }, [doc.version, doc.title, doc.body]);

  const who = WHO[doc.kind];
  const save = useMutation({
    mutationFn: () => live.publishLegal(doc.kind, { title, body, askAgain: who ? askAgain : false }),
    onSuccess: (r) => {
      setMsg(
        `Published as version ${r.version}. ` + (!who ? 'The apps show it on their next open.'
          : r.askAgain ? `Every ${who.slice(0, -1)} is asked to agree on their next open.`
          : `No one is asked again — ${r.carried} agreement${r.carried === 1 ? '' : 's'} carried forward.`),
      );
      void qc.invalidateQueries({ queryKey: ['legal'] });
    },
    onError: (e) => setMsg(errorText(e)),
  });
  const changed = title !== (doc.title ?? '') || body !== (doc.body ?? '');
  const label = LABEL[doc.kind];

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>{label}</h3>
      <div className="li-sub" style={{ marginBottom: 10 }}>
        {doc.version
          ? <>
              Version {doc.version} · published {when(doc.updatedAt)}{doc.updatedBy ? ` by ${doc.updatedBy}` : ''}
              {doc.requiresConsent === false && ' · a correction, nobody asked again'}
              {who && doc.acceptedCurrent != null && doc.people != null
                && <> · {doc.acceptedCurrent} of {doc.people} {who} agreed</>}
            </>
          : 'Not published yet.'}
      </div>
      <input className="field" value={title} placeholder="Title"
             onChange={(e) => { setTitle(e.target.value); setMsg(null); }} />
      <textarea className="field" rows={18} value={body}
                style={{ marginTop: 8, width: '100%', fontFamily: 'inherit', lineHeight: 1.5, resize: 'vertical' }}
                onChange={(e) => { setBody(e.target.value); setMsg(null); }} />
      <div className="li-sub" style={{ margin: '6px 0 10px' }}>
        Plain text. A blank line starts a new paragraph; a line starting with “- ” is a bullet.
        {' '}{body.length.toLocaleString()} characters.
      </div>
      {who && changed && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '0 0 12px', cursor: 'pointer' }}>
          <input type="checkbox" checked={askAgain} onChange={(e) => setAskAgain(e.target.checked)}
                 style={{ marginTop: 3 }} />
          <span>
            <b>Ask everyone to agree again</b>
            <div className="li-sub">
              {askAgain
                ? `Every ${who.slice(0, -1)} sees the new text and must tick “I agree” before using the app.`
                : `For a correction that changes nothing anyone agreed to — a typo, a phone number. ${
                    who[0]!.toUpperCase() + who.slice(1)} who agreed before are carried forward and not asked.`}
            </div>
          </span>
        </label>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn sm" disabled={!changed || save.isPending}
                onClick={() => {
                  const what = !who ? 'The apps show it at once.'
                    : askAgain ? `Every ${who.slice(0, -1)} will be asked to agree again.`
                    : `Nobody will be asked again.`;
                  if (window.confirm(`Publish a new version of the ${label}? ${what}`)) save.mutate();
                }}>
          {save.isPending ? 'Publishing…' : 'Publish new version'}
        </button>
        {changed && (
          <button className="btn ghost sm" onClick={() => { setTitle(doc.title ?? ''); setBody(doc.body ?? ''); }}>
            Discard changes
          </button>
        )}
        {msg && <span className="li-sub">{msg}</span>}
      </div>
    </div>
  );
}

export function Legal() {
  const { data, isLoading, error } = useQuery({ queryKey: ['legal'], queryFn: () => live.legal() });
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty title="Could not load the documents." hint={errorText(error)} />;

  const doc = (k: LegalDoc['kind']) => data.items.find((d) => d.kind === k);
  const terms = doc('terms');
  const agent = doc('agent_terms');
  const ratio = (d?: LegalDoc) => d?.acceptedCurrent != null && d.people != null
    ? `${d.acceptedCurrent} / ${d.people}` : '—';
  return (
    <>
      <div className="tiles">
        <Tile k="Terms" v={terms?.version ? `v${terms.version}` : '—'} d={`published ${when(terms?.updatedAt)}`} />
        <Tile k="Drivers agreed" v={ratio(terms)} d="to the current Terms" />
        <Tile k="Counter Terms" v={agent?.version ? `v${agent.version}` : '—'} d={`published ${when(agent?.updatedAt)}`} />
        <Tile k="Counters agreed" v={ratio(agent)} d="to the current Counter Terms" />
      </div>
      <h3 style={{ margin: '22px 0 0', fontSize: 15 }}>Driver app</h3>
      {terms && <Editor doc={terms} />}
      {doc('privacy') && <Editor doc={doc('privacy')!} />}
      <h3 style={{ margin: '22px 0 0', fontSize: 15 }}>Spark Agent (counters)</h3>
      {agent && <Editor doc={agent} />}
    </>
  );
}
